import { PSM, type Bbox, type Worker } from 'tesseract.js'
import { SYMBOL_REGEX, parsePricePoint } from './extractHvfDetails'

export type DataArea = { x: number; y: number; width: number; height: number }

// Where to look for the HVF data box, as fractions of the screenshot. It is drawn
// in the top right of the chart; the full image is only searched as a fallback.
const SEARCH_REGIONS = [
  { left: 0.5, top: 0, width: 0.5, height: 0.5 },
  { left: 0, top: 0, width: 1, height: 1 },
]

const PRICE_LINES = 6
const MIN_PRICE_LINES = 3

const drawRegion = (image: HTMLImageElement, region: (typeof SEARCH_REGIONS)[number]) => {
  const left = Math.round(image.naturalWidth * region.left)
  const top = Math.round(image.naturalHeight * region.top)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(image.naturalWidth * region.width)
  canvas.height = Math.round(image.naturalHeight * region.height)
  canvas.getContext('2d')?.drawImage(image, -left, -top)
  return { canvas, left, top }
}

/**
 * Finds the HVF data box (EXCHANGE:SYMBOL followed by the PRICE-TIME lines) in a
 * chart screenshot and returns its area in natural image pixels, or null.
 */
export const detectDataArea = async (worker: Worker, image: HTMLImageElement): Promise<DataArea | null> => {
  await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO })

  for (const region of SEARCH_REGIONS) {
    const { canvas, left, top } = drawRegion(image, region)
    const { data } = await worker.recognize(canvas, {}, { blocks: true })

    const words = (data.blocks ?? []).flatMap((block) =>
      block.paragraphs.flatMap((paragraph) => paragraph.lines.flatMap((line) => line.words)),
    )
    const priceBoxes = words.filter((word) => parsePricePoint(word.text) !== null).map((word) => word.bbox)

    if (priceBoxes.length < MIN_PRICE_LINES) {
      continue
    }

    const symbolBoxes = words.filter((word) => SYMBOL_REGEX.test(word.text.toUpperCase())).map((word) => word.bbox)
    const area = boundingArea(priceBoxes, symbolBoxes)

    // Back to full image coordinates, clamped to the image edges
    const x0 = Math.max(0, area.x + left)
    const y0 = Math.max(0, area.y + top)
    const x1 = Math.min(image.naturalWidth, area.x + left + area.width)
    const y1 = Math.min(image.naturalHeight, area.y + top + area.height)

    return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 }
  }

  return null
}

const boundingArea = (priceBoxes: Bbox[], symbolBoxes: Bbox[]): DataArea => {
  const sorted = [...priceBoxes].sort((a, b) => a.y0 - b.y0)
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  const lineHeight = Math.max(...sorted.map((box) => box.y1 - box.y0))

  // Distance between two rows: the smallest gap between detected lines, so lines
  // the OCR missed in between don't inflate it
  const gaps = sorted
    .slice(1)
    .map((box, i) => box.y0 - sorted[i].y0)
    .filter((gap) => gap > lineHeight * 0.8)
  const lineSpacing = gaps.length > 0 ? Math.min(...gaps) : lineHeight * 2.5

  // Extra room on the sides: OCR sometimes misses the leading "0" of a price
  const paddingX = lineHeight * 2
  const paddingY = lineHeight
  // Only trust a symbol found right above the price list
  const symbolBox = symbolBoxes.find(
    (box) => box.y0 < first.y0 && box.y0 > first.y0 - lineSpacing * 1.5,
  )

  // There are always PRICE_LINES rows. Rows the OCR could not read (tiny text,
  // chart lines crossing the text) must still be inside the crop: reserve room
  // for them below the last detected row, and above the first one too unless
  // the symbol line proves the first detected row is the top one.
  const detectedRows = Math.round((last.y0 - first.y0) / lineSpacing) + 1
  const missingRows = Math.max(0, PRICE_LINES - detectedRows)
  // +1 row above for the symbol line, included even if OCR missed it
  const rowsAbove = symbolBox ? 1 : 1 + missingRows

  let x0 = Math.min(...sorted.map((box) => box.x0))
  let x1 = Math.max(...sorted.map((box) => box.x1))
  let y0 = first.y0 - lineSpacing * rowsAbove
  const y1 = last.y1 + lineSpacing * missingRows

  if (symbolBox) {
    x0 = Math.min(x0, symbolBox.x0)
    x1 = Math.max(x1, symbolBox.x1)
    y0 = Math.min(y0, symbolBox.y0)
  }

  return {
    x: x0 - paddingX,
    y: y0 - paddingY,
    width: x1 - x0 + paddingX * 2,
    height: y1 - y0 + paddingY * 2,
  }
}
